import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { ChevronDown, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePreferencesStore, type Locale } from '../../../stores/preferences.js';
import { i18nInstance, LANGUAGE_LOCKED_TO_EN } from '../../../lib/i18n.js';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Fran\u00e7ais' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Espa\u00f1ol' },
] as const;

export function FooterLanguageSelector() {
  const { t } = useTranslation('legal');
  const locale = usePreferencesStore((s) => s.locale);
  const setLocale = usePreferencesStore((s) => s.setLocale);

  // While the app is locked to English the selector has nothing to offer.
  if (LANGUAGE_LOCKED_TO_EN) return null;

  const currentLabel =
    LANGUAGE_OPTIONS.find((o) => o.value === locale)?.label ?? LANGUAGE_OPTIONS[0].label;

  async function handleChange(newLocale: Locale) {
    try {
      await i18nInstance.changeLanguage(newLocale);
      setLocale(newLocale);
    } catch {
      /* language bundle failed to load — keep current locale */
    }
  }

  return (
    <Listbox value={locale} onChange={handleChange}>
      <div className="relative">
        <ListboxButton
          className="inline-flex items-center gap-1.5 text-caption text-secondary hover:text-accent-blue transition-colors duration-150 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          aria-label={t('languageSelector')}
        >
          <Globe className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{currentLabel}</span>
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        </ListboxButton>
        <ListboxOptions
          anchor="top"
          className="z-10 mb-2 bg-white/[.03] backdrop-blur-sm border border-white/[.04] rounded-lg overflow-hidden focus:outline-none transition data-[closed]:opacity-0 data-[open]:opacity-100 motion-safe:duration-100"
        >
          {LANGUAGE_OPTIONS.map((opt) => (
            <ListboxOption
              key={opt.value}
              value={opt.value}
              className="px-4 py-2 text-caption text-primary cursor-pointer data-[focus]:bg-white/[.05] data-[selected]:text-accent-lime"
            >
              {opt.label}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
}
